import { useState, DragEvent } from 'react';
import { Plus, X, BarChart3, Gauge, Table } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LayoutSlot } from '../types/Layout';
import { FormFactor } from '../utils/formFactor';
import { useDashboardDrag, DRAG_MIME_TYPE } from '../state/DashboardDragContext';
import { cn } from '@/infrastructure/lib/utils';

interface EmptySlotProps {
  slot: LayoutSlot;
  onPlusClick: (slot: LayoutSlot) => void;
  onDropDashboard: (slotId: string, dashboardBaseId: string) => void;
  onRemoveSlot?: (slotId: string) => void;
}

const FORM_FACTOR_ICON: Record<FormFactor, typeof BarChart3> = {
  tile: Gauge,
  graph: BarChart3,
  table: Table,
  hero: BarChart3,
};

const FORM_FACTOR_LABEL_KEY: Record<FormFactor, string> = {
  tile: 'dashboards.formFactor.tile',
  graph: 'dashboards.formFactor.graph',
  table: 'dashboards.formFactor.table',
  hero: 'dashboards.formFactor.hero',
};

const FORM_FACTOR_LABEL_FALLBACK: Record<FormFactor, string> = {
  tile: 'Tile',
  graph: 'Gráfico',
  table: 'Tabela',
  hero: 'Destaque',
};

const FORM_FACTOR_ACCENT: Record<FormFactor, string> = {
  tile: 'text-blue-500',
  graph: 'text-purple-500',
  table: 'text-emerald-500',
  hero: 'text-amber-500',
};

export const EmptySlot = ({ slot, onPlusClick, onDropDashboard, onRemoveSlot }: EmptySlotProps) => {
  const { t } = useTranslation();
  const { drag } = useDashboardDrag();
  const [isOver, setIsOver] = useState(false);

  const Icon = FORM_FACTOR_ICON[slot.formFactor] ?? BarChart3;
  const label = t(FORM_FACTOR_LABEL_KEY[slot.formFactor], FORM_FACTOR_LABEL_FALLBACK[slot.formFactor]);

  const externalDragActive = drag?.externalDashboardBaseId !== undefined;
  const isCompatible = !drag?.formFactor || drag.formFactor === slot.formFactor;

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!externalDragActive || !isCompatible) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsOver(true);
  };

  const handleDragLeave = () => setIsOver(false);

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!isCompatible) return;
    e.preventDefault();
    setIsOver(false);
    const dashboardBaseId =
      e.dataTransfer.getData(DRAG_MIME_TYPE) || e.dataTransfer.getData('text/plain');
    if (dashboardBaseId) {
      onDropDashboard(slot.id, dashboardBaseId);
    }
  };

  const dimmed = externalDragActive && !isCompatible;

  // Impede que mousedown em botões interativos propague para o root, que é
  // o drag-handle do react-grid-layout. Sem isso, clicar no "+" ou "X"
  // iniciaria um drag também.
  const stopDrag = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      role="region"
      aria-label={t('dashboards.layouts.emptySlotAria', 'Slot vazio do tipo {{type}}', { type: label })}
      className={cn(
        'dashboard-drag-handle group relative h-full w-full rounded-md border-2 border-dashed transition-all flex flex-col items-center justify-center gap-2 p-4 cursor-grab active:cursor-grabbing',
        'border-border bg-muted/30',
        isOver && 'border-primary bg-primary/10 scale-[1.01]',
        dimmed && 'opacity-30 pointer-events-none',
        externalDragActive && isCompatible && !isOver && 'border-primary/50'
      )}
    >
      <Icon className={cn('h-6 w-6 pointer-events-none', FORM_FACTOR_ACCENT[slot.formFactor])} />
      <span className={cn('text-xs font-medium pointer-events-none', FORM_FACTOR_ACCENT[slot.formFactor])}>{label}</span>
      <button
        type="button"
        onMouseDown={stopDrag}
        onClick={() => onPlusClick(slot)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPlusClick(slot);
          }
        }}
        className="rounded-full bg-background border border-border p-2 hover:bg-accent hover:border-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer"
        aria-label={t('dashboards.layouts.addToSlot', 'Adicionar dashboard')}
      >
        <Plus className="h-5 w-5 text-muted-foreground" />
      </button>
      {onRemoveSlot && (
        <button
          type="button"
          onMouseDown={stopDrag}
          onClick={() => onRemoveSlot(slot.id)}
          className="absolute top-2 right-2 rounded-full bg-background border border-border p-1 hover:bg-destructive/10 hover:border-destructive transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive cursor-pointer"
          aria-label={t('dashboards.layouts.removeSlot', 'Remover slot')}
        >
          <X className="h-3 w-3 text-muted-foreground" />
        </button>
      )}
    </div>
  );
};
